// Structured logging utility for production-ready error tracking
import { captureException } from '../lib/sentry';

/**
 * Log a structured error to the console
 * @param {string} source - The source of the error (e.g., "ApiService.fetchCreatives")
 * @param {string} message - Human-readable error summary
 * @param {Error} error - The original error object
 * @param {Object} context - Additional context (params, endpoint, etc.)
 */
export function logError(source, message, error, context = {}) {
  const structuredLog = {
    level: 'error',
    timestamp: new Date().toISOString(),
    source,
    message,
    context,
    error: {
      name: error?.name || 'UnknownError',
      message: error?.message || 'No error message available',
      stack: error?.stack || 'No stack trace available'
    }
  };

  console.error(JSON.stringify(structuredLog, null, 2));

  // Also log the original error for browser dev tools formatting
  console.error(`[${source}]`, error);

  // Report to Sentry for remote error tracking
  if (error instanceof Error) {
    captureException(error, { source, extra: context });
  }

  return structuredLog;
}

/**
 * Log a structured warning to the console
 * @param {string} source - The source of the warning
 * @param {string} message - Human-readable warning summary
 * @param {Object} context - Additional context
 */
export function logWarning(source, message, context = {}) {
  const structuredLog = {
    level: 'warning',
    timestamp: new Date().toISOString(),
    source,
    message,
    context
  };

  console.warn(JSON.stringify(structuredLog, null, 2));
  
  return structuredLog;
}

/**
 * Log a structured info message to the console
 * @param {string} source - The source of the info
 * @param {string} message - Human-readable info summary
 * @param {Object} context - Additional context
 */
export function logInfo(source, message, context = {}) {
  const structuredLog = {
    level: 'info',
    timestamp: new Date().toISOString(),
    source,
    message,
    context
  };

  console.log(JSON.stringify(structuredLog, null, 2));
  
  return structuredLog;
}
